/**
 * 서명 HTML 안의 이미지를 base64 data: URL 로 굳힌다 — 작성기에 서명이 삽입되는 시점에.
 *
 * <h3>왜 URL 을 그냥 두면 안 되나</h3>
 * <ol>
 *   <li><b>인증 URL</b>({@code /api/mail/signatures/images/…}) — X-TOKEN 이 필요한 엔드포인트다.
 *       브라우저의 native {@code <img>} 요청은 axios interceptor 가 붙이는 헤더를 못 싣고,
 *       받는 사람은 우리 인증 엔드포인트에 애초에 닿지 못한다. 양쪽 다 깨진다.</li>
 *   <li><b>사내 공개 URL</b>({@code https://onandme.info/mail-logo.png}) — 우리 화면에서는 뜨지만,
 *       Gmail·Outlook·네이버는 <b>수신 메일의 원격 이미지를 기본 차단</b>한다. 받는 사람이
 *       "이미지 표시" 를 누르기 전까지 로고 자리가 비어 보인다.</li>
 * </ol>
 *
 * <h3>data: 로 바꿔 두면 그 뒤는 전부 자동</h3>
 * <ul>
 *   <li>작성기 — 네트워크·인증과 무관하게 즉시 렌더</li>
 *   <li>발송 — 백엔드 {@code HtmlSanitizer.extractInlineImages} 가 data: → cid: 인라인 첨부
 *       (multipart/related)로 변환. 차단 해제 없이 바로 보인다.</li>
 *   <li>보낸편지함 — data: 그대로 보관, DOMPurify 의 {@code ADD_DATA_URI_TAGS:['img']} 로 렌더</li>
 * </ul>
 * 발송 메일에 base64 문자열이 실려 나가는 게 아니라 정상 첨부 파트가 된다 — 본문이 부풀지 않는다.
 *
 * <h3>남의 서버 이미지는 건드리지 않는다</h3>
 * 사내 자산이 아닌 외부 URL 까지 긁어 오면 (a) CORS 로 대부분 실패해 시간만 버리고
 * (b) 남의 트래커·대용량 이미지를 메일에 통째로 싣게 된다. 아래 {@link classify} 가 정한
 * 대상만 변환하고, 나머지는 원본 URL 그대로 둔다.
 *
 * <h3>실패해도 막지 않는다</h3>
 * 변환에 실패하면 원본 URL 을 그대로 남긴다 — 서명 삽입 자체가 실패하는 것보다 낫다.
 * 개발 환경(localhost:3000)에서 사내 공개 URL 은 교차 출처라 fetch 가 막히는데,
 * 이때는 같은 경로를 자기 origin 에서 한 번 더 시도한다 (CRA 가 public/ 를 그대로 서빙하므로
 * 운영과 같은 파일을 받는다). 그래도 안 되면 URL 유지.
 */
import { api as apiClient } from "../../../../util/api";

/** 사내 공개 에셋 호스트 — 여기 올라간 이미지는 로고·배너라 base64 로 굳혀도 안전한 크기다. */
const ASSET_HOSTS = ["onandme.info", "onnme.in"];

/** X-TOKEN 이 필요한 서명 이미지 프록시 (MailSignatureController). */
const AUTH_IMAGE_PREFIX = "/api/mail/signatures/images/";

/**
 * 이 src 를 어떻게 가져올지 결정한다.
 * @returns {"auth"|"public"|null} null 이면 손대지 않는다.
 */
function classify(src) {
    if (!src) return null;
    // 이미 data:/cid: 면 굳어 있다. blob: 은 다른 탭에서 못 여니 그대로 두면 안 되지만
    // 서명 본문에 들어올 경로가 없다.
    if (/^(data|cid):/i.test(src)) return null;

    let url;
    try {
        url = new URL(src, window.location.href);   // 상대 경로도 여기서 절대화된다
    } catch {
        return null;
    }
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;

    if (url.pathname.startsWith(AUTH_IMAGE_PREFIX)) return "auth";
    if (url.origin === window.location.origin)      return "public";
    if (ASSET_HOSTS.includes(url.hostname))         return "public";
    return null;
}

/** Blob → data: URL. */
const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
});

/** 공개 URL 하나를 받아 온다. 실패는 예외로 던져 호출부의 폴백을 태운다. */
async function fetchPublic(url) {
    const res = await fetch(url, { credentials: "omit", cache: "force-cache" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
}

/** src 하나를 data: URL 로. 못 가져오면 예외. */
async function toDataUrl(src, kind) {
    if (kind === "auth") {
        // axios 가 baseURL + X-TOKEN 을 붙인다. 절대 URL 이면 baseURL 은 무시되고 헤더만 실린다.
        const res  = await apiClient.download(src);
        const blob = res?.data ?? res;
        if (!blob) throw new Error("빈 응답");
        return blobToDataUrl(blob);
    }

    // 공개 URL — 원본 그대로 한 번, 막히면 같은 경로를 자기 origin 에서 한 번 더 (개발 환경용).
    const url  = new URL(src, window.location.href);
    const tries = [url.href];
    if (url.origin !== window.location.origin) tries.push(url.pathname + url.search);

    let lastError;
    for (const candidate of tries) {
        try {
            return await blobToDataUrl(await fetchPublic(candidate));
        } catch (e) { lastError = e; }
    }
    throw lastError;
}

/**
 * 서명 HTML 의 이미지들을 base64 로 치환한 HTML 을 돌려준다.
 *
 * <p>DOMParser 로 파싱하는 이유는 이 폴더의 다른 모듈들과 같다 — 살아있는 DOM 에 넣으면
 * 죽은 URL 로 요청이 나가고 {@code onerror} 가 실행될 수 있다. DOMParser 문서는 비활성이다.
 *
 * @param {string} html 저장된 서명 본문
 * @returns {Promise<string>} 변환된 HTML (대상이 없으면 원본 그대로)
 */
export async function inlineSignatureImages(html) {
    if (!html) return html;

    const doc  = new DOMParser().parseFromString(String(html), "text/html");
    const jobs = [];
    doc.body.querySelectorAll("img[src]").forEach((img) => {
        const src  = img.getAttribute("src");
        const kind = classify(src);
        if (kind) jobs.push({ img, src, kind });
    });
    if (jobs.length === 0) return html;

    // 같은 URL 이 여러 번 나와도 한 번만 받는다 (로고를 머리말·꼬리말 양쪽에 쓰는 서명).
    const cache = new Map();
    await Promise.all(jobs.map(async ({ img, src, kind }) => {
        try {
            if (!cache.has(src)) cache.set(src, toDataUrl(src, kind));
            const dataUrl = await cache.get(src);
            if (dataUrl) img.setAttribute("src", dataUrl);
        } catch (e) {
            console.warn("[Signature] 이미지 inline 변환 실패 — 원본 URL 유지", src, e);
        }
    }));

    return doc.body.innerHTML;
}
